import { Component, EventEmitter, forwardRef, Input, OnInit, Output, inject, ChangeDetectionStrategy } from '@angular/core';
import {Question} from '../../models/definition/question';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {PossibleAnswer} from '../../models/definition/possibleanswer';
import {IQuestionType} from '../../models';
import moment from 'moment';
import {RouterService} from '../../services/router/router.service';
import {TisPossibleAnswer} from '../../models/definition/tis-possible-answer';
import {IDepartment} from '../../models/department/department';

import {VWUICheckedEvent} from '@bvgo-ict/vwui-core';
import {INT32_MAX, INT32_MIN, NUMERIC_INPUT_PATTERN, createFinancialMask, createIntegerMask} from '../../utils/question-answer.util';
import {ObjectUtil} from "../../utils";

type QuestionValue = number | string | PossibleAnswer | TisPossibleAnswer | boolean | number[];

type ChangeFunction = (QuestionValueOutput) => void;

interface IVWUIInputvalueChanged {
    target: {
        value: string;
        valueAsDate?: Date;
    };
    stopPropagation: () => void;
}

@Component({
    selector: 'app-question-input',
    templateUrl: './question-input.component.html',
    styleUrls: ['./question-input.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => QuestionInputComponent),
            multi: true
        }
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class QuestionInputComponent implements OnInit, ControlValueAccessor {
    private routerService = inject(RouterService);

    @Input() public question: Question;
    @Input() public readonly: boolean;
    @Output() public valueChanged: EventEmitter<QuestionValue> = new EventEmitter<QuestionValue>();

    /** Used for departments type questions */
    @Input() public departments: IDepartment[] = [];

    public defaultTextAnswerLength = 1_024;

    public valueModel: QuestionValue;
    public QuestionType = IQuestionType;

    private onChangeFn: ChangeFunction;
    private onTouchedFn: ChangeFunction;

    public isPreviousUrlMandating: boolean;

    public get possibleAnswers(): (PossibleAnswer | TisPossibleAnswer)[] {
        return [...this.question.possibleAnswers, ...this.question.possibleTisAnswers];
    }

    public colorScoreIndicator(answer: PossibleAnswer | TisPossibleAnswer): string {
        switch ((answer as PossibleAnswer).colorScore) {
            case 'RED': return '\u{1F534} ';
            case 'ORANGE': return '\u{1F7E0} ';
            case 'GREEN': return '\u{1F7E2} ';
            default: return '';
        }
    }

    public get questionIsMandatory(): boolean {
        return ObjectUtil.isEmpty(this.valueModel) && this.isPreviousUrlMandating && this.question.isRequired;
    }

    private get numericMaskRange(): [number, number] {
        return [this.question.minValue ?? INT32_MIN, this.question.maxValue ?? INT32_MAX];
    }

    private integerMaskConfig?: Record<string, unknown>;

    public get integerMask(): Record<string, unknown> {
        if (!this.integerMaskConfig) {
            this.integerMaskConfig = createIntegerMask(...this.numericMaskRange);
        }

        return this.integerMaskConfig;
    }

    private financialMaskConfig?: Record<string, unknown>;

    public get financialMask(): Record<string, unknown> {
        if (!this.financialMaskConfig) {
            this.financialMaskConfig = createFinancialMask(...this.numericMaskRange);
        }

        return this.financialMaskConfig;
    }

    public get isMultiAnswer(): boolean {
        return this.question.isMultiAnswer === true;
    }

    ngOnInit() {
        this.isPreviousUrlMandating = this.routerService.checkIfFromMandating();
    }

    onTextChange({detail: {value}}): void {
        if (this.readonly) {
            return;
        }

        this.valueModel = value;
        this.onTouchedFn?.(value);
        this.onChangeFn?.(value);
        this.valueChanged.emit(value);
    }

    handleChange(newAnswer: QuestionValue | IVWUIInputvalueChanged | VWUICheckedEvent) {
        if (this.readonly) {
            return;
        }

        const newAnswerEvent = newAnswer as IVWUIInputvalueChanged;

        let formattedAnswer;

        switch (this.question.type) {
            case IQuestionType.DATE:
                formattedAnswer = moment(newAnswerEvent.target.value).format('YYYY-MM-DD');
                newAnswerEvent.stopPropagation();
                this.writeValue(formattedAnswer === 'Invalid date' ? null : formattedAnswer);
                break;
            case IQuestionType.TEXT:
                formattedAnswer = newAnswerEvent.target.value;
                this.writeValue(formattedAnswer);
                newAnswerEvent.stopPropagation();
                break;
            case IQuestionType.PERCENTAGE:
            case IQuestionType.INTEGER:
            case IQuestionType.FINANCIAL:
                formattedAnswer = this.sanitizeNumericAnswer(newAnswer as QuestionValue);
                this.writeValue(formattedAnswer);
                break;
            case IQuestionType.MULTIPLECHOICE:
            case IQuestionType.CALCULATION:
                formattedAnswer = newAnswer;
                this.writeValue(formattedAnswer);
                break;
            case IQuestionType.BOOL:
                // eslint-disable-next-line no-case-declarations
                const checkedEvent = newAnswer as VWUICheckedEvent;
                formattedAnswer = this.parseNullableStringAsBool(checkedEvent.value as string | null);
                this.writeValue(formattedAnswer);
                break;
            default:
                formattedAnswer = newAnswer;
                break;
        }

        this.onTouchedFn(formattedAnswer);
        this.onChangeFn(formattedAnswer);

        this.valueChanged.emit(formattedAnswer);
    }

    public toggleCheckbox(event: Event): void {
        event.preventDefault();

        if (this.readonly) {
            return;
        }

        // toggle from true to `null` and vice versa
        const newValue = this.valueModel === null ? true : null;
        this.writeValue(newValue);

        this.onTouchedFn(newValue);
        this.onChangeFn(newValue);

        this.valueChanged.emit(newValue);
    }

    filterItems(term: string, answerId: number): boolean {
        const {answer} = this.possibleAnswers.find(a => a.id === answerId);
        return answer.toLocaleLowerCase().includes(term.toLocaleLowerCase());
    }

    registerOnChange(fn: ChangeFunction): void {
        this.onChangeFn = fn;
    }

    registerOnTouched(fn: ChangeFunction): void {
        this.onTouchedFn = fn;
    }

    writeValue(value: QuestionValue): void {
        this.valueModel = value;

        // For the read only version, keep the answer set
        if (this.readonly && this.question.type === IQuestionType.DATE) {
            const changedValue: string = this.valueModel as string;
            if (changedValue) {
                this.valueModel = moment(changedValue).format('DD-MM-YYYY');
            }
        }
    }

    /**
     * The mask on the underlying vwui-input-mask element can be bypassed (e.g. pasting text),
     * so re-validate against a numbers-only pattern here and fall back to the last known-good value.
     */
    private sanitizeNumericAnswer(value: QuestionValue): QuestionValue {
        if (typeof value !== 'string') {
            return value;
        }

        if (value === '') {
            return value;
        }

        if (!NUMERIC_INPUT_PATTERN.test(value)) {
            return this.valueModel;
        }

        return Number(value);
    }

    private parseNullableStringAsBool(str: string | null): boolean | null {
        if (str == null) {
            return null;
        }
        return str === 'true';
    }
}
